import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import useProductImages, { cleanImage } from '../hooks/useProductImages';

// Sticky scroll sequence: the flagship case unpacks itself piece by piece.
const SPOTS = [
  { x: -36, y: -24, r: -7 },
  { x: -12, y: -30, r: 4 },
  { x: 14, y: -25, r: -3 },
  { x: 36, y: -4, r: 7 },
  { x: 21, y: 24, r: -5 },
  { x: -9, y: 30, r: 3 },
  { x: -35, y: 10, r: -6 },
  { x: 3, y: -1, r: 2 },
];

const Piece = ({ i, total, code, name, image, progress }) => {
  const spot = SPOTS[i % SPOTS.length];
  const span = 0.5 / Math.max(total, 1);
  const start = 0.1 + i * span;
  const end = Math.min(start + 0.34, 0.98);

  const x = useTransform(progress, [start, end], ['0vw', `${spot.x}vw`]);
  const y = useTransform(progress, [start, end], ['0vh', `${spot.y}vh`]);
  const rotate = useTransform(progress, [start, end], [0, spot.r]);
  const scale = useTransform(progress, [start, end], [0.55, 1]);
  const opacity = useTransform(progress, [start, start + span * 0.9], [0, 1]);

  return (
    <motion.div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{ x, y, rotate, scale, opacity }}
      data-testid={`unpack-piece-${code.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="w-[168px] xl:w-[196px] bg-mb-surface hairline border-subtle overflow-hidden">
        <div className="relative aspect-[4/5]">
          {image && (
            <img
              src={image}
              alt={name || code}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.opacity = 0;
              }}
            />
          )}
        </div>
        <div className="px-3 py-2 hairline-t border-subtle flex items-center justify-between">
          <span className="eyebrow-sm text-gold">{code}</span>
        </div>
      </div>
    </motion.div>
  );
};

export const UnpackHero = ({ set, products = {} }) => {
  const { manifest } = useProductImages();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });

  const items = (set?.items || []).slice(0, 8);
  const caseScale = useTransform(progress, [0, 0.28], [1, 0.72]);
  const caseOpacity = useTransform(progress, [0.06, 0.3], [1, 0.12]);
  const titleY = useTransform(progress, [0, 1], ['0vh', '-6vh']);
  const countdown = useTransform(progress, [0.1, 0.62], [0, items.length]);
  const revealed = useTransform(countdown, (v) => Math.min(items.length, Math.round(v)));

  if (!set) return null;

  return (
    <section
      className="hairline-t border-subtle"
      data-testid="home-unpack-section"
    >
      {/* Desktop: sticky scroll-scrubbed unpack */}
      <div ref={ref} className="hidden md:block relative h-[420vh]">
        <div className="sticky top-[72px] h-[calc(100vh-72px)] overflow-hidden">
          <div className="absolute inset-0">
            {/* the case */}
            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ scale: caseScale, opacity: caseOpacity }}
            >
              <div className="w-[360px] h-[240px] hairline border-gold bg-mb-surface flex items-center justify-center">
                <div className="text-center">
                  <div className="eyebrow text-gold">The set, unpacked</div>
                  <div className="mt-4 font-display italic text-[18px] text-mb-muted">
                    {set.name}
                  </div>
                </div>
              </div>
            </motion.div>

            {items.map((code, i) => (
              <Piece
                key={code}
                i={i}
                total={items.length}
                code={code}
                name={products[code]?.name}
                image={cleanImage(manifest, code)}
                progress={progress}
              />
            ))}
          </div>

          {/* copy overlay */}
          <motion.div
            className="relative z-10 h-full max-w-[1400px] mx-auto px-12 flex flex-col justify-between py-14 pointer-events-none"
            style={{ y: titleY }}
          >
            <div className="max-w-sm">
              <div className="eyebrow text-gold">Scroll to unpack</div>
              <h2 className="mt-6 font-display text-4xl xl:text-5xl leading-[1.05] text-mb">
                {set.name}
              </h2>
              <p className="mt-6 font-display italic text-[17px] text-mb-muted">
                {set.tagline}
              </p>
            </div>

            <div className="flex items-end justify-between">
              <div className="eyebrow-sm text-mb-muted max-w-[220px] leading-relaxed">
                {set.leather}
                <br />
                {set.laptop ? `${set.laptop} laptop` : 'Presented by hand'}
              </div>
              <div className="text-right pointer-events-auto">
                <div className="font-display text-5xl xl:text-6xl text-mb tabular-nums">
                  <motion.span data-testid="unpack-counter">{revealed}</motion.span>
                  <span className="text-mb-muted">/{items.length}</span>
                </div>
                <div className="mt-3 eyebrow-sm text-mb-muted">Pieces revealed</div>
                <Link
                  to={`/sets/${set.slug}`}
                  className="mt-6 inline-flex btn-outline-gold"
                  data-magnetic
                  data-testid="unpack-set-cta"
                >
                  Open the set
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Mobile: static composed grid */}
      <div className="md:hidden max-w-[1400px] mx-auto px-6 py-20">
        <div className="eyebrow text-gold">The flagship</div>
        <h2 className="mt-5 font-display text-4xl text-mb">{set.name}</h2>
        <p className="mt-4 font-display italic text-[16px] text-mb-muted">
          {set.tagline}
        </p>
        <div className="mt-10 grid grid-cols-2 gap-4">
          {items.map((code) => (
            <div key={code} className="bg-mb-surface hairline border-subtle overflow-hidden">
              <div className="relative aspect-[4/5]">
                {cleanImage(manifest, code) && (
                  <img
                    src={cleanImage(manifest, code)}
                    alt={products[code]?.name || code}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.opacity = 0;
                    }}
                  />
                )}
              </div>
              <div className="px-3 py-2 hairline-t border-subtle eyebrow-sm text-gold">
                {code}
              </div>
            </div>
          ))}
        </div>
        <Link to={`/sets/${set.slug}`} className="mt-10 btn-outline-gold w-full">
          Open the set
        </Link>
      </div>
    </section>
  );
};

export default UnpackHero;
